import { doc, setDoc, getDoc, getDocs, updateDoc, collection, writeBatch } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import QRCode from 'qrcode';
import { saveUserLog } from './LogandUtils';
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

/**
 * Data URL → Blob 변환
 */
function dataURLtoBlob(dataurl) {
  const arr = dataurl.split(',');
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : '';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
}

/**
 * QR 코드 생성 후 Storage 업로드 및 Firestore 저장
 */
async function generateAndSaveQRCode(musicianId, uid) {
  try {
    const qrCodeURL = `https://musizic.com/qr/${musicianId}`;
    const qrCodeFileName = `${musicianId}_QR.png`;
    const qrCodePathInStorage = `users/${uid}/${qrCodeFileName}`;
    
    const qrCodeDataURL = await QRCode.toDataURL(qrCodeURL, {
      width: 500,
      scale: 10,
      margin: 2,
    });

    const qrCodeBlob = dataURLtoBlob(qrCodeDataURL);

    const storage = getStorage();
    const storageRef = ref(storage, qrCodePathInStorage);
    const snapshot = await uploadBytes(storageRef, qrCodeBlob, {
      contentType: 'image/png',
    });

    const publicURL = await getDownloadURL(snapshot.ref);

    const qrDocRef = doc(db, "musicians", musicianId, "style", "main");
    await setDoc(qrDocRef, {
      qrimg: publicURL,
    }, { merge: true });

    return publicURL;
  } catch (error) {
    console.error("QR 코드 생성 또는 저장 중 오류:", error);
  }
}

export async function CreateInitialMusicard(formData, musicParam, pushAlbums, setLoading, setLoadingMessage, onSuccess) {
  const uid = auth.currentUser?.uid;
  if (!uid || !formData.id) return;

  const musicardId = formData.id.trim();
  const userRef = doc(db, "users", uid);
  const userDataRef = doc(db, "users_data", uid);
  const musicianRef = doc(db, "musicians", musicardId);
  let styleAlbumsForImage = [];

  try {
    setLoading(true);
    setLoadingMessage("Step 1: 뮤지카드 생성 중...");
    
    await setDoc(musicianRef, {
      id: musicardId,
      ownerId: uid,
      positions: formData.positions
    });

    const userSnap = await getDoc(userRef);
    const userData = userSnap.data();
    const prefix = formData.positions.includes("Group/Band") ? "team" : "solo";
    const currentKeys = userData.musicard
      ? Object.keys(userData.musicard).filter(key => key.startsWith(`${prefix}_`))
      : [];
    const newKey = `${prefix}_${(currentKeys.length + 1).toString().padStart(2, '0')}`;

    await setDoc(userRef, {
      musicard: { [newKey]: musicardId }
    }, { merge: true });

    // QR 코드 생성 및 Firebase Storage에 업로드 + Firestore 저장
    const qrImage = await generateAndSaveQRCode(musicardId, uid);

    await saveUserLog('musicard_logs', musicardId, 'create', musicianRef.path, '뮤지카드 최초 생성');

    
    // Step 2
    setLoadingMessage("Step 2: 기본 정보 저장 중...");

    await updateDoc(musicianRef, {
      name: formData.name,
      genres: formData.genres,
      positions: formData.positions,
      profileImage: formData.profileImage,
    });

    await saveUserLog('musicard_logs', musicardId, 'update', musicianRef.path, '기본 정보 업데이트');

    // Step 3: 앨범 데이터 복사 및 스타일 트랙 수집
    setLoadingMessage("Step 3: 앨범 데이터 복사 중...");

    const selectedType = musicParam === "released" ? "ownAlbums" : "unreleasedAlbums";
    let highestTrack = null;
    let albumWithHighestTrack = null;
    let styleAlbumsForImage = [];

    for (const album of pushAlbums) {
    const albumId = album.id;

    // 1. users_data에서 앨범 조회
    const userAlbumRef = doc(db, `users_data/${uid}/${musicParam}/${albumId}`);
    const albumSnap = await getDoc(userAlbumRef);
    if (!albumSnap.exists()) {
        console.warn(`앨범 ${albumId}을 찾을 수 없습니다`);
        continue;
    }

    const albumData = albumSnap.data();

    // 2. musicians 컬렉션으로 복사
    const albumRef = doc(db, `musicians/${musicardId}/${selectedType}/${albumId}`);
    await setDoc(albumRef, {
        ...albumData,
        albumId,
        ownerId: uid
    }, { merge: true });

    // 3. 트랙 복사 및 최고 playCount 트랙 수집
    const tracksRef = collection(db, `users_data/${uid}/${musicParam}/${albumId}/tracks`);
    const tracksSnap = await getDocs(tracksRef);

    const batch = writeBatch(db);
    tracksSnap.forEach((trackDoc) => {
        const destTrackRef = doc(db, `musicians/${musicardId}/${selectedType}/${albumId}/tracks`, trackDoc.id);
        batch.set(destTrackRef, trackDoc.data());

        // 💡 최고 조회수 트랙 추출
        const data = trackDoc.data();
        const playCount = parseInt(data.playCount);
        if (
        data.url &&
        !isNaN(playCount) &&
        (!highestTrack || playCount > parseInt(highestTrack.playCount || 0))
        ) {
        highestTrack = data;
        albumWithHighestTrack = albumData; // ✅ 이 앨범의 cover도 기억
        }
    });
    await batch.commit();

    // 4. users_data 앨범에 act 필드 병합
    await setDoc(userAlbumRef, {
        act: { [musicardId]: selectedType }
    }, { merge: true });

    await saveUserLog('musicard_logs', musicardId, 'create', albumRef.path, `앨범(${albumId}) push 및 트랙 복사`);

    // 💡 스타일 이미지용 앨범 수집
    if (!styleAlbumsForImage.some(a => a.id === albumId) && albumData.cover) {
        styleAlbumsForImage.push({ id: albumId, cover: albumData.cover });
    }
    }

    // Step 4: 스타일 데이터 저장
    setLoadingMessage("Step 4: 스타일 데이터 저장 중...");

    let videoId = "", videoTitle = "";

    if (highestTrack?.url) {
    const url = highestTrack.url;
    if (url.includes("youtube.com") || url.includes("youtu.be")) {
        const match = url.match(/(?:v=|\/)([0-9A-Za-z_-]{11})/);
        videoId = match ? match[1] : "";
    } else if (url.includes("soundcloud.com")) {
        videoId = url;
    }
    videoTitle = highestTrack.title || "";
    }

    const menuImages = styleAlbumsForImage
    .filter(album => album.cover)
    .slice(0, 4)
    .map(album => album.cover);

    const userDataSnap = await getDoc(userDataRef);
    const users_data = userDataSnap.data();
    const userLinks = users_data?.musicLinks || {};
    let CDThumbnail = albumWithHighestTrack?.cover || "";

    const styleRef = doc(db, "musicians", musicardId, "style", "main");
    await setDoc(styleRef, {
    videoId,
    videoTitle,
    menuImages,
    CDThumbnail, // 🎯 여기 추가
    musicLinks: userLinks
    }, { merge: true });

    await saveUserLog('musicard_logs', musicardId, 'update', styleRef.path, '스타일 정보 생성 및 저장');

    // Step 5: 연락처 적용
    setLoadingMessage("Step 5: 연락처 데이터 적용 중...");

    const contactSnaps = await getDocs(collection(db, `users_data/${uid}/contact`));
    const latestContact = contactSnaps.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(c => c.id.startsWith("Initial Contact_"))
        .sort((a, b) => parseInt(b.id.split("_")[1]) - parseInt(a.id.split("_")[1]))
        .at(0); // 가장 최신 항목  

    if (latestContact) {
      const personalRef = doc(db, `musicians/${musicardId}/contact`, "personal");
      await setDoc(personalRef, {
        name: latestContact.name,
        //email: latestContact.email || "",
        introduction: latestContact.introduction || "",
        sns: latestContact.sns || {}
      }, { merge: true });

      const sourceRef = doc(db, `users_data/${uid}/contact/${latestContact.id}`);
      await setDoc(sourceRef, {
        act: { [musicardId]: "personal" }
      }, { merge: true });

      await saveUserLog('musicard_logs', musicardId, 'create', personalRef.path, '최신 Initial Contact 적용');
    }

    
    // 뮤지카드 활성화
    await updateDoc(doc(db, "musicians", musicardId), {
        act: true,
    });
    

    onSuccess(`/musicard/${musicardId}`, qrImage ?? '');  // 🎯 부모에서 isSuccess, redirectUrl 상태 바꿀 수 있도록
    return {
      success: true,
      message: "뮤지카드가 성공적으로 생성되었습니다!",
      redirectUrl: `/musicard/${musicardId}`
    };
  } catch (error) {
    console.error("뮤지카드 생성 실패:", error);
    return {
      success: false,
      message: "뮤지카드 생성 중 오류가 발생했습니다."
    };
  }
}